import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import apiClient from "@/api/client";
import { downloadBlob } from "@/utils/download";
import {
  useAronApiBase,
  useAronConversationInfo,
  useAronMessages,
  useAronWorkModes,
} from "../api/aronApi";
import { ARON_PRINT_CSS, AronConversationPrint } from "../components/AronConversationPrint";

/**
 * "Drukuj / PDF" and "Pobierz zapis" for one conversation. A hook, not a
 * component, because the same two actions are offered from the pane header
 * (dock, ENG-ARON) and from the tab menu in the workbench.
 */
export function useAronConversationExport(conversationId: string | null) {
  const apiBase = useAronApiBase();
  const { data: messages = [] } = useAronMessages(conversationId);
  // Identity of the thread for the printout header: which preset answered, on which
  // model, and under which write mode. Falls back to the raw model id when the preset
  // is not in the picker any more (hidden or retired) — a printout must not go blank
  // just because a mode was taken off the menu.
  const conversation = useAronConversationInfo(conversationId);
  const { data: workModes } = useAronWorkModes();
  const mode = workModes?.reasoning_modes.find(
    (m) => m.value === conversation?.reasoning_mode,
  );
  const [exporting, setExporting] = useState(false);

  const downloadLog = async () => {
    if (!conversationId) return;
    setExporting(true);
    try {
      const r = await apiClient.get(`${apiBase}/conversations/${conversationId}/export/`);
      const blob = new Blob([JSON.stringify(r.data.data, null, 2)], {
        type: "application/json",
      });
      downloadBlob(blob, `aron-rozmowa-${conversationId.slice(0, 8)}.json`);
    } finally {
      setExporting(false);
    }
  };

  const printConversation = () => {
    if (!conversationId || messages.length === 0) return;
    const title = "ARON — zapis rozmowy";
    const generatedAt = new Date().toLocaleString("pl-PL");
    const body = renderToStaticMarkup(
      <AronConversationPrint
        title={title}
        subject={conversation?.title}
        messages={messages}
        generatedAt={generatedAt}
        mode={mode}
        modelId={conversation?.model_id}
        autopilot={conversation?.autopilot}
      />,
    );
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return; // pop-up blocked
    w.document.write(
      `<!doctype html><html lang="pl"><head><meta charset="utf-8">` +
        `<title>${title}</title>` +
        // Same two families the app itself uses — without them the printout falls
        // back to a system font and stops looking like the conversation on screen.
        `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
        `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?` +
        `family=IBM+Plex+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap">` +
        `<style>${ARON_PRINT_CSS}</style></head>` +
        `<body>${body}</body></html>`,
    );
    w.document.close();
    w.focus();
    // Give the new window a beat to lay out before opening the print dialog.
    let printed = false;
    const fire = () => {
      if (printed) return;
      printed = true;
      w.print();
    };
    w.onload = fire;
    setTimeout(fire, 400);
  };

  return {
    exporting,
    downloadLog,
    printConversation,
    hasMessages: messages.length > 0,
  };
}
